// src/pages/finance-portal/reports/TrialBalance.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack, Chip,
} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, todayIso, downloadCsv, printReport, AccountLink,
} from './reportShared';

export default function TrialBalance() {
    const [asOf, setAsOf] = useState(todayIso());
    const [tbType, setTbType] = useState('ADJUSTED');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/trial-balance', {
                params: { as_of: asOf, type: tbType },
            });
            if (res.data && res.data.success) {
                setData({
                    ...res.data,
                    accounts: Array.isArray(res.data.accounts) ? res.data.accounts : [],
                });
            } else {
                setError(res.data?.message || 'Failed to load');
            }
        } catch (e) {
            console.error('Trial Balance load error:', e);
            setError(e.response?.data?.message || e.message || 'Failed to load trial balance');
        } finally { setLoading(false); }
    }, [asOf, tbType]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        downloadCsv(`trial_balance_${asOf}.csv`, data.accounts || [],
            ['account_number', 'account_name', 'account_type', 'debit', 'credit']);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ListAltIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Trial Balance</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <TextField label="As of" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={tbType} onChange={(e) => setTbType(e.target.value)}>
                                <MenuItem value="UNADJUSTED">Unadjusted</MenuItem>
                                <MenuItem value="ADJUSTED">Adjusted</MenuItem>
                                <MenuItem value="POST_CLOSING">Post-Closing</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 3 }} id="tb-report">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Trial Balance ({data.tb_type})</Typography>
                            <Typography variant="body2" color="text.secondary">As of {data.as_of}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<PrintIcon />} onClick={() => printReport('tb-report')}>Print</Button>
                            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account #</TableCell>
                                    <TableCell>Account Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Debit</TableCell>
                                    <TableCell align="right">Credit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.accounts || []).map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>
                                            <AccountLink
                                                accountId={r.account_id}
                                                label={r.account_number}
                                                asOf={asOf}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <AccountLink
                                                accountId={r.account_id}
                                                label={r.account_name}
                                                asOf={asOf}
                                            />
                                        </TableCell>
                                        <TableCell>{r.account_type}</TableCell>
                                        <TableCell align="right">
                                            {r.debit > 0 ? formatCurrency(r.debit) : ''}
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.credit > 0 ? formatCurrency(r.credit) : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '3px double #111827', fontWeight: 700 } }}>
                                    <TableCell colSpan={3}>Totals</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_debit)}</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_credit)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2 }}>
                        <Chip label={data.balanced ? 'Balanced ✓' : 'Imbalance!'}
                            color={data.balanced ? 'success' : 'error'} />
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
